import type { SpProgramView } from '@evi/shared';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard, AlertCircle,
} from 'lucide-react';
import { KpiCard, RevenueChart } from '@evi/ui';
import { useTheme } from '@evi/skins';
import { useAuthStore } from '@/store/auth';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import {
  listAgents, listAgentPayouts, fetchChargers, fetchActiveSessions,
  type AgentRow,
} from '@/lib/api';
import { formatRelativeTime } from '@/pages/dashboard/widgets/_lib';
import {
  commissionAxis, commissionCharts, commissionCurrencies, commissionTotals,
  formatCommissionRow, formatCommissionTotals, type CommissionRow,
} from '@/lib/spCommissionMoney';
import { DashboardMapBackground } from '@/pages/dashboard/DashboardMapBackground';
import { SalesPartnerLocationInfoCard } from './SalesPartnerLocationInfoCard';
import { SalesPartnerConnectBanner } from '@/components/sp/SalesPartnerConnectBanner';
import { goldenLadderLines, goldenPercent, programPercent, tierProgress } from '@/lib/tierProgress';
import { agentPayoutStatusLabel, type PayoutStatusTone } from '@/lib/agentPayoutStatus';
import { QuotePipelineCard } from '@/pages/partner-quotes/QuotePipelineCard';
import { ServiceCallsCard } from '@/pages/service-requests/ServiceCallsCard';
import { PartnerTeamCard } from '@/pages/sales-team/SalesJobsPage';
import styles from './SalesPartnerDashboardPage.module.css';

/**
 * Phase 7g.4b.2.5 , Sales Partner Dashboard, operator-mirror pivot.
 *
 * Same data layer as 7g.4b.2 (commit 2a8b953). Layout pivots from
 * a vertical stack to the operator dashboard's shape: satellite
 * map background + left-rail widgets + persistent top banner.
 *
 * Structure mirror of operator DashboardPage (DashboardPage.tsx):
 *   - .page wrapper at z-index 2 sitting above the map (z-index 0)
 *     and vignettes (z-index 1). TopNav stays at z-index 50.
 *   - .mapBackground sibling at position:fixed, top:64px (below
 *     TopNav), spanning the viewport. Hosts DashboardMapBackground
 *     with an InfoCardComponent prop pointing at the SP variant.
 *   - .vignetteTop + .vignetteBottom soften the map → page-chrome
 *     transition at the top + bottom edges. Theme-scoped via
 *     .pageDark / .pageLight modifier.
 *   - Top banner sits fixed below TopNav, spans the viewport,
 *     z-index 1. The .page below adds top padding when the banner
 *     renders so the rail content doesn't slip behind the banner.
 *   - Left-rail widgets pin to cols 1-4 of a 12-col grid; the map
 *     breathes through cols 5-12.
 *
 * Data layer (preserved byte-equivalent from 7g.4b.2):
 *   - 4 React Query hooks (listAgents, listAgentPayouts,
 *     fetchChargers, fetchActiveSessions)
 *   - KPI derivations: commission this month / lifetime / pending /
 *     chargers assigned / active sessions
 *   - Chart bucket helper: daily, oldest-first, cents → dollars
 *   - Ledger: last 10 payouts, most-recent first
 *   - Empty ledger copy unchanged
 *   - Connect-success URL param dismissible banner unchanged
 *
 * Map data: DashboardMapBackground internally fetches
 * /dashboard/map-points which is SP-scoped per Phase 7g.4a
 * (chargerIds derived from Charger.find({ agent_id: linkedAgentId }),
 * locationIds derived from those chargers, cross-tenant). SP sees
 * markers ONLY at locations where they have assigned chargers.
 * Empty SP (no chargers) renders no markers; CameraController
 * handles zero-markers gracefully (no auto-fit attempted on empty
 * bounds , the map defaults to its initial center/zoom).
 *
 * Map info card: the SP variant ships with simpler stats than
 * operator's (Chargers Online + Active Sessions + session-revenue
 * chart). Per-location commission breakdown lands in 7g.4b.3 with
 * the agent-payouts projection extension that adds charger_id to
 * AgentOpsQueueRow.
 */

interface Props {
  onNavigate?: (to: string) => void;
}

type ChartPeriod = '7d' | '30d';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Pending statuses for the "owed to me" KPI. Three states count
 *  as money owed to the SP that hasn't landed in their Connect
 *  account yet:
 *    'pending'              waiting to be transferred to Stripe
 *    'failed'               transfer attempt failed, in retry cron queue
 *    'pending_stripe_setup' agent hasn't completed Stripe Connect
 *                           onboarding yet (Phase 3a state added in
 *                           7g.4b.3 Commit 1; SPs blocked on Connect
 *                           previously didn't see these counted)
 *  Excluded states:
 *    'transferred'          terminal success
 *    'reversed'             terminal refund settled
 *    'absorbed_clawback'    terminal refund absorbed to clawback debt
 *    'permanently_failed'   retry cron gave up; not pending anymore,
 *                           awaits manual ops resolution
 *    'clawback_pending'     reversal lifecycle state, distinct from
 *                           forward-transfer pending semantic
 */
const PENDING_STATUSES = new Set<string>(['pending', 'failed', 'pending_stripe_setup']);

export function SalesPartnerDashboardPage({ onNavigate }: Props) {
  // Renamed from 'Dashboard' with the nav leaf (Warren, 2026-09-24):
  // a sales partner thinks in installs. The operator Dashboard leaf is
  // a DIFFERENT leaf and keeps its name.
  useRecentRoute('My Installs');
  const user = useAuthStore((s) => s.user);
  const agentId = user?.agent_id ?? null;

  // Theme mode for the map basemap style. Light flips to
  // mapbox/light-v11; dark stays on satellite. Same hook
  // operator DashboardPage uses.
  const { mode, theme } = useTheme();
  // Cooperative gestures on mobile + tablet (single-finger pans
  // the page, two-finger pans the map). Free gestures on desktop.
  // Mirror of operator's useViewport check at DashboardPage.tsx:263.

  // ── Chart period (7d / 30d). 24h dropped , payout cadence makes
  // hourly bucketing too sparse to read. Matches Reports' Energy
  // tab pattern (it ships 30d-only via allowedPeriods). ─────────
  const [period, setPeriod] = useState<ChartPeriod>('7d');

  // ── Data (preserved byte-equivalent from 7g.4b.2) ────────────
  //
  // Four React Query hooks. listAgentPayouts is the primary call ,
  // backs four downstream surfaces. The other three single-purpose
  // queries each back one KPI / banner state.
  //
  // staleTime: 30s across the board. Matches the operator dashboard
  // cadence (useOperatorSummary uses 30s too). Chart chip taps stay
  // instant on cache hits.
  const agentsQ = useQuery({
    queryKey: ['sp-dashboard', 'agents', agentId],
    queryFn: listAgents,
    enabled: !!agentId,
    staleTime: 30_000,
  });
  const payoutsQ = useQuery({
    queryKey: ['sp-dashboard', 'payouts', agentId],
    queryFn: () => {
      // The `enabled` flag below gates this on agentId being
      // non-null, but TypeScript can't narrow across the closure.
      // Capture into a local + early-return throws to satisfy the
      // type checker without the lint-warned non-null assertion.
      if (!agentId) throw new Error('agentId required');
      return listAgentPayouts(agentId);
    },
    enabled: !!agentId,
    staleTime: 30_000,
  });
  const chargersQ = useQuery({
    queryKey: ['sp-dashboard', 'chargers'],
    // limit:100 covers any realistic SP charger count in a single
    // page; the SP-scoped /chargers response per 7g.4a returns ONLY
    // chargers assigned to their Agent. .meta.total is the
    // authoritative count regardless of page size.
    queryFn: () => fetchChargers({ limit: 100 }),
    staleTime: 30_000,
  });
  const onlineChargersQ = useQuery({ queryKey: ['sp-dashboard', 'online-chargers', agentId], queryFn: () => fetchChargers({ limit: 1, is_online: true }), enabled: !!agentId, staleTime: 30_000 });
  const activeSessionsQ = useQuery({
    queryKey: ['sp-dashboard', 'active-sessions'],
    queryFn: fetchActiveSessions,
    staleTime: 30_000,
  });

  // The SP's own Agent row. listAgents is SP-scoped per 7g.4a to
  // return array-of-one (their own); find by id defensively in case
  // the array shape ever changes server-side.
  const agent: AgentRow | null = useMemo(() => {
    if (!agentsQ.data || !agentId) return null;
    return agentsQ.data.find((a) => a.id === agentId) ?? null;
  }, [agentsQ.data, agentId]);

  const payouts = useMemo<CommissionRow[]>(
    () => payoutsQ.data?.rows ?? [],
    [payoutsQ.data],
  );

  // Every currency the partner's commission is in: the rows' own, else the
  // payout account's. Tiles and charts show one figure per currency and
  // never add two together (Warren, 2026-10-16).
  const currencies = useMemo(
    () => commissionCurrencies(payouts, agent?.payout_country),
    [payouts, agent?.payout_country],
  );

  // Derived KPI values, one total per currency.
  const kpis = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

    let pendingCount = 0;
    for (const p of payouts) {
      if (PENDING_STATUSES.has(p.status)) pendingCount += 1;
    }

    return {
      commissionToday: commissionTotals(payouts, (p) => new Date(p.created_at).toDateString() === now.toDateString()),
      commissionThisMonth: commissionTotals(payouts, (p) => new Date(p.created_at).getTime() >= monthStart),
      commissionLifetime: commissionTotals(payouts),
      pendingCount,
      chargersAssigned: chargersQ.data?.meta.total ?? 0,
      activeSessions: activeSessionsQ.data?.length ?? 0,
    };
  }, [payouts, chargersQ.data, activeSessionsQ.data]);

  // Chart buckets: daily, oldest first, one chart per currency.
  const charts = useMemo(() => {
    const days = period === '7d' ? 7 : 30;
    const now = new Date();
    const todayEnd = new Date(
      now.getFullYear(), now.getMonth(), now.getDate(),
      23, 59, 59, 999,
    ).getTime();
    return commissionCharts(payouts, currencies, days, (p) =>
      days - 1 - Math.floor((todayEnd - new Date(p.created_at).getTime()) / MS_PER_DAY));
  }, [payouts, period, currencies]);

  // ── Ledger (preserved byte-equivalent) ──────────────────────
  const ledgerRows = useMemo(() => {
    return [...payouts]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 10);
  }, [payouts]);

  // ── Profile-incomplete signal (replaces the old full-screen
  // no-agent early-return). is_sales_agent true but agent_id null
  // (data drift / pre-provisioning). The page now renders its map +
  // rail UNCONDITIONALLY; the SalesPartnerConnectBanner surfaces
  // this state as a non-blocking top banner instead of replacing the
  // whole page. Queries are already gated on agentId
  // (enabled: !!agentId) so they stay idle , KPIs read zeros, the
  // ledger shows its empty state, the map renders with no markers ,
  // until provisioning lands and a /auth/me reconcile (or re-login)
  // flips agent_id live.
  const profileIncomplete = (user?.is_sales_agent ?? false) && !agentId;

  // ── Loading skeleton (preserved). Renders without the map
  // background to avoid the satellite-tile flash before data settles.
  const initialLoading = (agentsQ.isLoading && !agentsQ.data)
    || (payoutsQ.isLoading && !payoutsQ.data);
  if (initialLoading) {
    return (
      <div className={`${styles.page} ${styles.pageDark}`}>
        <PageHeader />
        <Skeleton />
      </div>
    );
  }

  const fatalError = agentsQ.error || payoutsQ.error
    ? (agentsQ.error instanceof Error ? agentsQ.error.message
      : payoutsQ.error instanceof Error ? payoutsQ.error.message
        : 'Failed to load dashboard')
    : null;

  const themeClass = mode === 'light' ? styles.pageLight : styles.pageDark;

  return (
    <div className={`${styles.page} ${styles.pageWithMap} ${themeClass}`}>
      {/* Full-bleed satellite map background. Position:fixed in CSS
          so it escapes the AppShell's 1400px content container and
          spans edge-to-edge below the TopNav. The map host fills
          .mapBackground; the InfoCardComponent prop routes pin
          clicks to the SP variant of the location info card. */}
      <div className={styles.mapBackground}>
        <DashboardMapBackground
          key={`${theme.id}-${mode}`}
          mode={mode}
          fitToPoints
          onNavigate={onNavigate}
          InfoCardComponent={SalesPartnerLocationInfoCard}
        />
      </div>
      {/* Soft top + bottom gradients soften the map → page chrome
          transition. Same shape + token references as operator
          DashboardPage's vignettes. */}
      <div className={styles.vignetteTop} aria-hidden />
      <div className={styles.vignetteBottom} aria-hidden />

      {/* The payout banner no longer sits fixed above the page (Warren, 21 October 2026: payout setup must not block
          anything, and a new partner submits jobs without it). It is a card in "Your account" below. */}

      {/* ── Left rail. Cols 1-4 of an implicit 12-col model.
          Contains header + KPI grid + chart + ledger stacked
          vertically. The map breathes through cols 5-12 (no
          widgets there). */}
      <div className={styles.rail}>
        <PageHeader />

        {fatalError && (
          <div className={styles.errorBanner}>
            <AlertCircle size={16} />
            <span>{fatalError}</span>
          </div>
        )}

        {/* KPI 2x2 cluster (4 financial tiles) + Pending Payouts
            standalone tile below. Operator dashboard uses an
            identical 2x2 cluster for its KPI block; SP gets one
            extra tile for the actionable pending count. */}
        {/* The first thing a partner does (walkthrough A 041): start a job, or quote one first. */}
        <div className={styles.ctaRow}>
          <a className={styles.cta} href="/sales-jobs/new" onClick={e => { if (onNavigate) { e.preventDefault(); onNavigate('/sales-jobs/new'); } }}>Start a new job</a>
          <a className={styles.ctaGhost} href="/sales-quotes/new" onClick={e => { if (onNavigate) { e.preventDefault(); onNavigate('/sales-quotes/new'); } }}>Build a quote</a>
          <a className={styles.ctaLink} href="/sales-jobs" onClick={e => { if (onNavigate) { e.preventDefault(); onNavigate('/sales-jobs'); } }}>Your jobs</a>
        </div>

        {/* Phone and tablet only: a clear strip of map, ahead of the
            cards, that can be touched and dragged. It used to sit after
            the earnings tiles (Warren, 2026-10-20 morning), which put it
            a full screen down on a phone while the tiles covered the map;
            Warren's 390px report the same day asked for a clear strip.
            data-map-window tells the camera to frame the location in it.
            Hidden on desktop, where the map shows beside the rail. */}
        <div className={styles.mapWindow} data-map-window aria-hidden />

        <div className={styles.kpiCluster}>
          <KpiCard label="Commission today" value={formatCommissionTotals(kpis.commissionToday, currencies)} meta="Recorded amounts, includes pending" />
          <KpiCard
            label="Commission this month"
            value={formatCommissionTotals(kpis.commissionThisMonth, currencies)}
            // Same divergence as the lifetime tile one column over, at a
            // smaller scale: this sums every row in the month, while
            // Commissions' "Commission this period" counts settled rows
            // only. Saying so here keeps the page internally honest.
            meta="Includes pending"
          />
          {/* "Lifetime earned", not "Commission lifetime" (Warren's
              ruling, 2026-09-24). The Commissions leaf one row below
              shows a DIFFERENT number under what used to be the same
              label: this tile counts every payout row including the
              ones still pending, that one counts only what has settled.
              Both are right; showing them as one name was not. Neither
              page is allowed to print a lifetime figure without saying
              which of the two it is. */}
          <KpiCard
            label="Lifetime earned"
            value={formatCommissionTotals(kpis.commissionLifetime, currencies)}
            meta="Includes pending"
          />
          <KpiCard
            label="Chargers online"
            value={String(onlineChargersQ.data?.meta.total ?? 0)}
            meta={`${kpis.chargersAssigned} assigned`}
          />
          <KpiCard
            label="Active sessions"
            value={String(kpis.activeSessions)}
          />
        </div>
        <div className={styles.kpiPending}>
          <KpiCard
            label="Pending payouts"
            value={String(kpis.pendingCount)}
          />
        </div>

        {/* Commission progress (EVI_SP_PROGRAM_SPEC; sales comp change,
            27 October 2026). Standard: the countdown to qualification, then
            the one rate. Golden: the next step on the Golden ladder.
            Renders only once the SP's Agent row is loaded. */}
        {agent && (
          <SalesPartnerTierCard
            program={agent.program_info}
            deployed={agent.deployed_paid_count ?? 0}
            activity={agent.activity ?? null}
          />
        )}

        {/* Quotes on Today (Warren, 21 October 2026): sent, opened, won and the win rate. */}
        <QuotePipelineCard onNavigate={onNavigate} />
        {/* Service calls on the partner's sites (ruling H, 21 October 2026); nothing when there are none. */}
        <ServiceCallsCard onNavigate={onNavigate} />

        {/* Your account (Warren, 21 October 2026): the profile, payouts and teammate invites live on the home, below
            the main action and never in the way of a job. */}
        <div className={styles.ledgerCard}>
          <div className={styles.ledgerHeader}>
            <h2 className={styles.ledgerTitle}>Your account</h2>
            <a className={styles.ledgerLink} href="/me" onClick={e => { if (onNavigate) { e.preventDefault(); onNavigate('/me'); } }}>Your profile →</a>
          </div>
          <SalesPartnerConnectBanner agent={agent} agentId={agentId} profileIncomplete={profileIncomplete} appearance="card" />
        </div>
        {agent && <PartnerTeamCard />}

        {/* Commission chart, one card per currency, each titled with its
            own currency. RevenueChart handles its own all-zero empty
            state internally. */}
        {charts.map((chart) => (
          <div key={chart.key} className={styles.chartCard}>
            <RevenueChart
              data={chart.data}
              currentRate={0}
              period={period}
              onPeriodChange={(next) => setPeriod(next as ChartPeriod)}
              allowedPeriods={['7d', '30d']}
              showRate={false}
              titleLabel={chart.title}
              emptyNoun="commission"
              formatY={commissionAxis(chart)}
            />
          </div>
        ))}

        {/* Recent commission ledger (preserved). */}
        <div className={styles.ledgerCard}>
          <div className={styles.ledgerHeader}>
            <h2 className={styles.ledgerTitle}>Recent commissions</h2>
            <button
              type="button"
              className={styles.ledgerLink}
              onClick={() => onNavigate?.(`/agents/${agentId}`)}
            >
              View all →
            </button>
          </div>
          {ledgerRows.length === 0 ? (
            <div className={styles.ledgerEmpty}>
              Your commission history will appear here once sessions run
              on your assigned chargers.
            </div>
          ) : (
            <table className={styles.ledgerTable}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Charger</th>
                  <th>Location</th>
                  <th className={styles.numCell}>Commission</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {ledgerRows.map((row) => (
                  <tr key={row.id}>
                    <td>{formatRelativeTime(row.created_at)}</td>
                    <td>
                      {/* charger_identity comes from the admin/agent-payouts.ts projection. A charger deleted out of
                          band reads Not available, never a session id (no developer copy, Warren, 28 October 2026). */}
                      {row.charger_identity ?? (
                        <span className={styles.cellMuted}>Not available</span>
                      )}
                    </td>
                    <td>
                      {row.location_name ?? (
                        <span className={styles.cellMuted}>Not available</span>
                      )}
                    </td>
                    <td className={styles.numCell}>
                      {formatCommissionRow(row)}
                    </td>
                    <td>
                      <StatusPill status={row.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

/** Page chrome , header with icon + title + subtitle. Visual mirror
 *  of operator DashboardPage header (line 379-397) minus the edit-
 *  mode actions on the right. */
function PageHeader() {
  return (
    <div className={styles.header}>
      <div className={styles.headerLeft}>
        <div className={styles.headerIcon}><LayoutDashboard size={18} /></div>
        <div>
          <h1 className={styles.title}>My Installs</h1>
          <p className={styles.subtitle}>Your chargers, their sessions, and what they earn you.</p>
        </div>
      </div>
    </div>
  );
}

/** Status pill for the ledger. Compact color coding for the five
 *  AgentPayoutStatus values. Matches the chip vocabulary on
 *  AgentDetailPage so SPs see the same status taxonomy across
 *  surfaces. */
function StatusPill({ status }: { status: string }) {
  // CSS module class lookups fall back to `pillMuted` for defensive
  // narrowing. Same pattern AgentStatusBadge uses.
  const fallback = styles.pillMuted ?? '';
  const tones: Record<PayoutStatusTone, string> = {
    ok: styles.pillOk ?? fallback, info: styles.pillInfo ?? fallback, warn: styles.pillWarn ?? fallback, muted: fallback,
  };
  // Words for every status, never the raw key (lib/agentPayoutStatus.ts).
  const c = agentPayoutStatusLabel(status);
  return <span className={`${styles.pill} ${tones[c.tone]}`}>{c.label}</span>;
}

// ── Commission tier card ─────────────────────────────────────────────
//
// The SP's commission in words (EVI_SP_PROGRAM_SPEC; sales comp change,
// 27 October 2026). The rates come from the Api's program view
// (spProgramView in @evi/shared), never from constants here. Standard
// has one rate on every qualifying charger: before qualification (fewer
// than 4 pay-to-use chargers) the card counts down to it, then states it,
// with no growth line and no ladder. Golden shows its next step and the
// four-step Golden ladder.

function SalesPartnerTierCard({ deployed, program, activity }: {
  deployed: number; program?: SpProgramView; activity: AgentRow['activity'];
}) {
  if (!program) return <div className={styles.tierCard}>Loading program details.</div>;
  const progress = tierProgress(deployed, program), golden = program.program === 'golden';
  // In a contractor's words (Warren, 21 October 2026): what you earn and when it starts; for Golden, the next step. A point
  // is one percent of each paid session, so rates read as percents; the cap is explained, never "points of a share".
  return <div className={styles.tierCard}>
    <div className={styles.tierTop}><span className={styles.tierLabel}>{program.name}</span>
      {program.eligible && <span className={styles.tierRate}>{programPercent(program.current_rate_bps, program.program)} of each session</span>}</div>
    {progress && <p className={styles.tierRate} style={{ margin: '4px 0' }}>{progress.headline}</p>}
    {progress?.detail && <p className={styles.tierMeta}>{progress.detail}</p>}
    <p className={styles.tierStatus}>{deployed} charger{deployed === 1 ? '' : 's'} installed so far.{program.eligible ? ' Your commission is on.' : ''}</p>
    <p className={styles.tierMeta}>{golden ? `Your Golden rate can grow to ${goldenPercent(program.cap_bps)} of each session as you install more. ` : ''}Your commission is paid from EVI's 15% share of each session.</p>
    {program.pending.map(p => <p key={p.effective_at} className={styles.tierMeta}>Scheduled: {p.program === 'golden' ? 'Golden Partner' : 'Sales Partner'} on {new Date(p.effective_at).toLocaleString()}.</p>)}
    {golden && <details><summary>How your Golden rate grows</summary><ul>{goldenLadderLines(program.ladder).map(line => <li key={line}>{line}</li>)}</ul>
      <p>Golden rates apply to chargers launched after you enrolled. A charger keeps the rate it launched with.</p></details>}
    {/* The yearly pace matters only once the commission is on; on day one it read as a warning (walkthrough A 041). */}
    {activity && program.eligible && <p className={styles.tierMeta}>Active partners install about {activity.min} chargers a year. You have installed {activity.deployed_last_window} in the last 12 months{activity.on_pace ? ', so you are on pace.' : `; ${activity.behind_by} more puts you on pace.`}</p>}
  </div>;
}

/** First-paint skeleton. Mirror of OperatorDashboardSkeleton at
 *  apps/admin/src/pages/dashboard/DashboardPage.tsx:572-593. */
function Skeleton() {
  const tile = (h: number): React.CSSProperties => ({
    height: h,
    background: 'var(--evi-surface)',
    border: '1px solid var(--evi-border)',
    borderRadius: 'var(--evi-radius-lg, 14px)',
    boxShadow: 'var(--evi-card-glow-subtle, none)',
    animation: 'evi-skeleton-pulse 1.6s ease-in-out infinite',
  });
  return (
    <div aria-busy="true" aria-live="polite" style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'var(--admin-stats-columns, repeat(2, 1fr))', gap: 12 }}>
        {Array.from({ length: 4 }).map((_, i) => <div key={i} style={tile(92)} />)}
      </div>
      <div style={tile(92)} />
      <div style={tile(320)} />
      <div style={tile(280)} />
    </div>
  );
}
